import React, { useState, useCallback, useRef } from 'react';
import { Paperclip, PushPin, Lightbulb, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LockedFeature from '../LockedFeature';
import './MessageInput.css';

// Mirrors the backend's allowed attachment types/size (see
// communicationService.uploadAttachment) so we can reject an invalid
// file client-side instead of letting the upload fail after send.
const ALLOWED_ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10MB

function formatFileSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

/**
 * MessageInput Component
 * 
 * Standalone message composition component with:
 * - Student context selection
 * - Message type classification (grade, attendance, behavior, fee, urgent, general)
 * - Character count tracking
 * - Keyboard shortcuts (Enter to send, Shift+Enter for a new line)
 * - Rich input formatting hints (icons for attachments, emoji picker)
 * - Loading/disabled states during sending
 * 
 * @param {Function} onSendMessage - Callback when message is sent: { content, student_id, message_type }
 * @param {Array} students - Optional array of student objects: { id, name }
 * @param {boolean} isSending - Loading state for send button
 * @param {string} userRole - Current user's role (for context in message)
 */
const MessageInput = ({ 
  onSendMessage, 
  students = [],
  isSending = false,
  userRole = 'Educator'
}) => {
  const [messageText, setMessageText] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [messageType, setMessageType] = useState('general');
  const [charCount, setCharCount] = useState(0);
  const [showEmojiHint, setShowEmojiHint] = useState(false);
  const [showAttachmentHint, setShowAttachmentHint] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const MAX_CHARS = 2000;

  // Trigger the hidden file input from the paperclip button
  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    // Reset the input value so selecting the same file again still fires onChange
    e.target.value = '';
    if (!file) return;

    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      toast.error('Only PDF, JPEG, PNG, and GIF files can be attached');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error('Attachment must be 10MB or smaller');
      return;
    }

    setSelectedFile(file);
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  // Handle message text change
  const handleTextChange = useCallback((e) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARS) {
      setMessageText(text);
      setCharCount(text.length);
    }
  }, []);

  // Send message
  const handleSend = useCallback(() => {
    if (!messageText.trim() || isSending) return;

    onSendMessage({
      content: messageText.trim(),
      student_id: selectedStudent || null,
      message_type: messageType,
      subject: `${messageType.charAt(0).toUpperCase() + messageType.slice(1)} from ${userRole}`,
      file: selectedFile
    });

    // Reset form
    setMessageText('');
    setCharCount(0);
    setSelectedStudent('');
    setMessageType('general');
    setSelectedFile(null);
  }, [messageText, selectedStudent, messageType, isSending, onSendMessage, userRole, selectedFile]);

  // Handle send with multiple keyboard shortcuts
  const handleKeyPress = useCallback((e) => {
    // Enter = send, Shift+Enter = new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Show emoji picker hint on focus
  const handleInputFocus = useCallback(() => {
    setShowEmojiHint(true);
  }, []);

  // Clear hints on blur
  const handleInputBlur = useCallback(() => {
    setShowEmojiHint(false);
    setShowAttachmentHint(false);
  }, []);

  // Character warning threshold (80% of max)
  const charWarningThreshold = Math.floor(MAX_CHARS * 0.8);
  const showCharWarning = charCount > charWarningThreshold;

  return (
    <div className="message-input-wrapper">
      {/* Input Options Row */}
      <div className="input-options-row">
        <div className="input-options-group">
          {/* Student Selection */}
          <select 
            className="input-select student-select"
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            disabled={isSending}
            title="Select student context (optional)"
          >
            <option value="">General Discussion</option>
            <optgroup label="Students">
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.name}
                </option>
              ))}
            </optgroup>
          </select>

          {/* Message Type Selector */}
          <select 
            className="input-select message-type-select"
            value={messageType}
            onChange={(e) => setMessageType(e.target.value)}
            disabled={isSending}
            title="Classify message type for better organization"
          >
            <option value="general">💬 General</option>
            <option value="grade">📊 Grade Update</option>
            <option value="attendance">📋 Attendance</option>
            <option value="behavior">⭐ Behavior</option>
            <option value="fee">💰 Fee</option>
            <option value="urgent">⚠️ Urgent</option>
          </select>
        </div>

        {/* Action Buttons Row */}
        <div className="input-actions-group">
          {/* Emoji Picker Button (UI Only) */}
          <button
            className="icon-button emoji-button"
            title="Add emoji (coming soon)"
            disabled={isSending}
            aria-label="Emoji picker"
          >
            😊
          </button>

          {/* Attachment Button */}
          <LockedFeature module="comms_premium">
            <button
              type="button"
              className="icon-button attachment-button"
              title="Attach a file (PDF, JPEG, PNG, GIF — max 10MB)"
              disabled={isSending}
              aria-label="Attach files"
              onClick={handleAttachClick}
            >
              <Paperclip size={18} />
            </button>
          </LockedFeature>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Selected Attachment Preview */}
      {selectedFile && (
        <div className="selected-attachment-row">
          <span className="selected-attachment-chip">
            <Paperclip size={14} />
            <span className="selected-attachment-name">{selectedFile.name}</span>
            <span className="selected-attachment-size">({formatFileSize(selectedFile.size)})</span>
            <button
              type="button"
              className="selected-attachment-remove"
              onClick={handleRemoveFile}
              disabled={isSending}
              aria-label="Remove attachment"
              title="Remove attachment"
            >
              <X size={12} />
            </button>
          </span>
        </div>
      )}

      {/* Message Composition Area */}
      <div className="message-composition-area">
        <textarea
          className="message-textarea"
          placeholder="Type your message here... (Enter to send, Shift+Enter for a new line)"
          value={messageText}
          onChange={handleTextChange}
          onKeyPress={handleKeyPress}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={isSending}
          rows={4}
          spellCheck="true"
          autoCapitalize="sentences"
        />

        {/* Character Count */}
        <div className={`char-counter ${showCharWarning ? 'warning' : ''}`}>
          <span className="char-count">{charCount}</span>
          <span className="char-max">/ {MAX_CHARS}</span>
        </div>
      </div>

      {/* Send Button & Info Row */}
      <div className="input-footer-row">
        <div className="input-info">
          {selectedStudent && (
            <span className="selected-student-badge">
              <PushPin size={14} /> Student context selected
            </span>
          )}
          {messageType !== 'general' && (
            <span className={`message-type-badge message-type-${messageType}`}>
              {messageType}
            </span>
          )}
        </div>

        <button
          className="send-btn"
          onClick={handleSend}
          disabled={!messageText.trim() || isSending}
          title={!messageText.trim() ? 'Type a message to send' : 'Send message'}
        >
          {isSending ? (
            <>
              <span className="spinner-dot"></span>
              Sending...
            </>
          ) : (
            <>
              Send
              <span className="send-icon">→</span>
            </>
          )}
        </button>
      </div>

      {/* Keyboard Shortcuts Hint */}
      {showEmojiHint && (
        <div className="input-hint">
          <Lightbulb size={14} /> Press <kbd>Shift+Enter</kbd> for new line • <kbd>Ctrl+Enter</kbd> to send
        </div>
      )}
    </div>
  );
};

export default MessageInput;
